import { useEffect } from 'react';

/**
 * Remember the clinic's patient-workspace configuration across page loads.
 *
 * `patient_workspace_settings` is read from local SQLite, which resolves a beat AFTER
 * first paint. For that beat every consumer sees the built-in fallback rather than the
 * clinic's actual configuration, and then visibly corrects itself:
 *
 *   - the Patient Overview lights up the Patient Timeline tab, then jumps to the
 *     Treatment Record;
 *   - the patient sidebar renders expanded, then collapses.
 *
 * Both read as the app second-guessing itself. This cache removes the guess: these are
 * clinic-level settings that change approximately never, so the last known values are a
 * near-perfect predictor, and the first paint of every visit after the first is already
 * correct.
 *
 * The cache is a HINT, never the source of truth. The live query overwrites it as soon
 * as it lands, so an admin changing a setting takes effect on the next paint. Being
 * briefly wrong is bounded by that, and only possible on the single load after a change.
 *
 * Keyed per org so switching tenants cannot inherit the previous clinic's configuration.
 */

const STORAGE_PREFIX = 'powercareai:workspace-settings:';

/** The subset worth caching: the settings that shape the FIRST paint. */
export interface CachedWorkspaceSettings {
  /** 'treatment_record' | 'full_workspace' — decides the landing tab. */
  workspaceMode: string;
  /** 'collapsed' | 'expanded' — decides the sidebar's initial state. */
  defaultSidebarState: string;
}

function storageKey(orgId: string): string {
  return `${STORAGE_PREFIX}${orgId}`;
}

/** Last known settings for this org, or null when nothing usable is cached. */
export function readCachedWorkspaceSettings(orgId: string): CachedWorkspaceSettings | null {
  if (!orgId) return null;
  try {
    // Storage can be unavailable (private mode, blocked cookies) and the stored value
    // can be stale JSON from an older shape. A miss is always safe — it costs only the
    // one-beat correction this exists to avoid.
    const raw = localStorage.getItem(storageKey(orgId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedWorkspaceSettings> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    const { workspaceMode, defaultSidebarState } = parsed;
    if (typeof workspaceMode !== 'string' || typeof defaultSidebarState !== 'string') return null;
    return { workspaceMode, defaultSidebarState };
  } catch {
    return null;
  }
}

function writeCachedWorkspaceSettings(orgId: string, value: CachedWorkspaceSettings): void {
  if (!orgId) return;
  try {
    localStorage.setItem(storageKey(orgId), JSON.stringify(value));
  } catch {
    // Ignore — the cache is an optimisation, never required for correctness.
  }
}

/**
 * Keep the cache in step with the loaded settings.
 *
 * Writes only once the query has actually loaded, so the built-in fallbacks seen during
 * loading are never mistaken for the clinic's real configuration and persisted over a
 * correct cached value — which would make the very bug this fixes sticky.
 */
export function useCacheWorkspaceSettings(
  orgId: string,
  value: CachedWorkspaceSettings,
  loaded: boolean,
): void {
  const { workspaceMode, defaultSidebarState } = value;
  useEffect(() => {
    if (!loaded || !orgId) return;
    writeCachedWorkspaceSettings(orgId, { workspaceMode, defaultSidebarState });
  }, [orgId, workspaceMode, defaultSidebarState, loaded]);
}

/** Whether the cached configuration says the sidebar starts collapsed. */
export function cachedSidebarStartsCollapsed(orgId: string): boolean {
  return readCachedWorkspaceSettings(orgId)?.defaultSidebarState === 'collapsed';
}
